'use client';

import { use, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Container, Section } from '@/components/primitives';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { BookCard } from '@/components/book-card';
import { Icon } from '@/components/icons';
import { clientListBooks, clientFacets, clientCategories } from '@/lib/client-api';
import type { Book, Category } from '@/types/catalog';

const PAGE = 24;
const SORTS: Array<{ id: string; label: string; params: Record<string, string> }> = [
  { id: 'title', label: 'A · Z', params: { sort: 'title', order: 'asc' } },
  { id: 'reciente', label: 'Más recientes', params: { sort: 'recent' } },
  { id: 'anio', label: 'Por año', params: { sort: 'year', order: 'desc' } },
];

interface Option { id: string; label: string }

function FacetGroup({
  title,
  options,
  value,
  onChange,
}: {
  title: string;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(true);
  if (options.length === 0) return null;
  return (
    <div style={{ borderBottom: '1px solid var(--rule)', padding: '18px 0' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: 'var(--ink)',
          fontFamily: 'var(--sans)',
          fontWeight: 500,
          fontSize: 14,
        }}
      >
        <span>{title}</span>
        <span style={{ transition: 'transform 160ms ease', transform: open ? 'rotate(180deg)' : 'none', color: 'var(--stone)' }}>▾</span>
      </button>
      {open && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 260, overflowY: 'auto' }}>
          {options.map((o) => (
            <label key={o.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: 'var(--ink-soft)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="check"
                checked={value === o.id}
                onChange={() => onChange(value === o.id ? '' : o.id)}
              />
              <span style={{ flex: 1 }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CategoryPage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params);

  const [cat, setCat] = useState<Category | null>(null);
  const [facets, setFacets] = useState<{ authors: string[]; years: number[]; subjects: string[] }>({ authors: [], years: [], subjects: [] });
  const [author, setAuthor] = useState('');
  const [subject, setSubject] = useState('');
  const [year, setYear] = useState('');
  const [sort, setSort] = useState('title');
  const [books, setBooks] = useState<Book[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  // Titles of the selected author across the whole catalog (other collections
  // and titles without one) and inside this collection with no other filter.
  // The facet only narrows inside this collection, so the notice compares the
  // two author-only counts, never the subject/year-filtered result total.
  const [authorCount, setAuthorCount] = useState<{ key: string; total: number; inCollection: number }>({
    key: '',
    total: 0,
    inCollection: 0,
  });
  const authorKey = `${slug}\u0000${author}`;
  // Counts loaded for another author (or collection) are never shown.
  const authorCounts = author && authorCount.key === authorKey ? authorCount : null;
  const showAuthorElsewhere = Boolean(
    authorCounts && authorCounts.inCollection > 0 && authorCounts.total > authorCounts.inCollection,
  );
  const authorTotal = authorCounts?.total ?? 0;

  useEffect(() => {
    let active = true;
    clientCategories().then((cats) => { if (active) setCat(cats.find((c) => c.slug === slug) ?? null); });
    clientFacets(slug).then((f) => { if (active) setFacets(f); });
    return () => { active = false; };
  }, [slug]);

  const load = useCallback(
    async (nextOffset: number, append: boolean) => {
      setLoading(true);
      const sortParams = SORTS.find((s) => s.id === sort)?.params ?? {};
      const res = await clientListBooks({ category: slug, author, subject, year, ...sortParams, limit: PAGE, offset: nextOffset });
      setTotal(res.total);
      setBooks((prev) => (append ? [...prev, ...res.items] : res.items));
      setOffset(nextOffset);
      setLoading(false);
    },
    [slug, author, subject, year, sort],
  );

  useEffect(() => { load(0, false); }, [load]);

  useEffect(() => {
    if (!author) return;
    let active = true;
    const key = `${slug}\u0000${author}`;
    Promise.all([
      clientListBooks({ author, limit: 1 }),
      clientListBooks({ category: slug, author, limit: 1 }),
    ]).then(([all, inCollection]) => {
      if (active) setAuthorCount({ key, total: all.total, inCollection: inCollection.total });
    });
    return () => { active = false; };
  }, [slug, author]);

  const clearFilters = () => { setAuthor(''); setSubject(''); setYear(''); };
  const hasFilters = Boolean(author || subject || year);
  const name = cat?.name ?? slug;
  const description = cat?.description ?? '';

  return (
    <div className="page-fade">
      <Header current="category" />

      <Section pad="lg">
        <Container>
          <nav aria-label="Migas" style={{ fontSize: 13, color: 'var(--stone)', marginBottom: 28 }}>
            <Link href="/" className="under" style={{ textDecoration: 'none', color: 'var(--stone)' }}>Inicio</Link>
            <span style={{ margin: '0 10px' }}>/</span>
            <Link href="/colecciones" className="under" style={{ textDecoration: 'none', color: 'var(--stone)' }}>Colecciones</Link>
            <span style={{ margin: '0 10px' }}>/</span>
            <span style={{ color: 'var(--ink)' }}>{name}</span>
          </nav>

          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 40, alignItems: 'end' }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 14 }}>Colección</div>
              <h1 className="serif" style={{ margin: 0, fontSize: 56, lineHeight: 1.02, letterSpacing: '-0.02em', fontWeight: 600 }}>
                {name}
              </h1>
              {description && <p style={{ marginTop: 16, color: 'var(--ink-soft)', fontSize: 16, maxWidth: 560 }}>{description}</p>}
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--stone)', textAlign: 'right', lineHeight: 1.8 }}>
              <div>{total} títulos disponibles</div>
              <div>30 días por préstamo</div>
            </div>
          </div>
        </Container>
      </Section>

      <hr className="rule-strong" />

      <Section pad="md">
        <Container>
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 56 }}>
            <aside style={{ position: 'sticky', top: 84, alignSelf: 'start' }}>
              <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Icon.filter /> Filtros
              </div>
              <FacetGroup title="Autor" options={facets.authors.map((a) => ({ id: a, label: a }))} value={author} onChange={setAuthor} />
              <FacetGroup title="Año" options={facets.years.map((y) => ({ id: String(y), label: String(y) }))} value={year} onChange={setYear} />
              <FacetGroup title="Tema" options={facets.subjects.map((s) => ({ id: s, label: s }))} value={subject} onChange={setSubject} />

              {hasFilters ? (
                <button className="btn btn-ghost" style={{ marginTop: 18, padding: '8px 14px', fontSize: 13 }} onClick={clearFilters}>
                  Limpiar filtros
                </button>
              ) : null}
            </aside>

            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 24,
                  paddingBottom: 16,
                  borderBottom: '1px solid var(--rule)',
                }}
              >
                <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
                  <strong>{total}</strong> resultados
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--stone)' }}>Ordenar por</span>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="input"
                    style={{ width: 'auto', padding: '7px 30px 7px 12px', fontSize: 13 }}
                  >
                    {SORTS.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {showAuthorElsewhere ? (
                <div
                  data-testid="author-elsewhere"
                  style={{ marginBottom: 24, padding: '12px 16px', border: '1px solid var(--rule)', fontSize: 14, color: 'var(--ink-soft)' }}
                >
                  Esta colección muestra parte de la obra de {author}: hay {authorTotal} títulos en toda la biblioteca.{' '}
                  <Link href={`/autor?name=${encodeURIComponent(author)}`} className="under" style={{ color: 'var(--ink)', fontWeight: 500 }}>
                    Ver todos los títulos del autor
                  </Link>
                </div>
              ) : null}

              {books.length === 0 && !loading ? (
                <div style={{ padding: '48px 0', color: 'var(--stone)', fontSize: 15 }}>
                  No hay títulos que coincidan con estos filtros.
                </div>
              ) : (
                <div className="m-book-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 190px)', gap: 36, justifyContent: 'space-between' }}>
                  {books.map((b) => (
                    <BookCard key={b.id} book={b} width={190} />
                  ))}
                </div>
              )}

              {books.length < total ? (
                <div style={{ marginTop: 56, display: 'flex', justifyContent: 'center' }}>
                  <button className="btn btn-ghost" disabled={loading} onClick={() => load(offset + PAGE, true)}>
                    {loading ? 'Cargando…' : 'Cargar más títulos'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </Container>
      </Section>

      <Footer />
    </div>
  );
}
